<template>
  <div>
    <div
      v-if="isLoadingList"
      :style="{ height: '80vh' }"
      class="d-flex flex-column justify-content-center align-items-center"
    >
      <CSpinner
        class="custom-spinner"
        color="info"
        grow
      />
    </div>
    
    <div v-else>
      <div class="d-flex justify-content-center align-items-center">
        <CInput
          v-model="filterInput"
          v-c-tooltip="{ content: 'Type to search accounts by account Id, account manager, company name, representative name or email address', placement: 'left' }"
          class="mt-4 ml-4"
          style="flex: 0 0 40%;"
          placeholder="Search"
          add-input-classes="col-6"
          :lazy="500"
        />

        <CSelect
          :value.sync="filterDisableReason"
          class="mt-4 ml-3"
          style="flex: 0 0 28%;"
          add-label-classes="text-sm-right pr-2 font-weight-bold"
          label="Filter by Active Status"
          :horizontal="{ label: 'col-sm-5', input: 'col-sm-7'}"
          :options="disableReasonFilterOptions"
        />

        <CSelect
          :value.sync="filterAccountManager"
          class="mt-4 ml-3"
          style="flex: 0 0 28%;"
          add-label-classes="text-sm-right pr-2 font-weight-bold"
          label="Filter by Account Manager"
          :horizontal="{ label: 'col-sm-6', input: 'col-sm-6'}"
          :options="accountManagersList"
        />
      </div>  

      <CCallout color="info">
        <small class="text-muted">Total Accounts:</small><br>
        <strong class="h4">
          {{ filteredAccounts.length }}
        </strong>
      </CCallout>

      <CDataTable
        data-cy="table-accounts"
        responsive
        small
        border
        sorter
        :items="filteredAccounts"
        :fields="fields"
        :items-per-page="10"
        :pagination="{ align: 'center' }"
      >
        <template #authorizeRingbaToContact="{item}">
          <td>
            <CBadge :color="getBooleanBadge(item.authorizeRingbaToContact)">
              {{ item.authorizeRingbaToContact ? 'Yes' : 'No' }}
            </CBadge>
          </td>
        </template>

        <template #name="{item}">
          <td>
            <CBadge
              v-if="item.enabled === false && item.disableReason"
              class="mr-1"
              :color="getDisableReasonBadgeColor(item.disableReason)"
            >
              {{ item.disableReason }}
            </CBadge>

            {{
              item.name
            }}

            <CBadge
              v-if="item.testOnly"
              color="danger"
              class="ml-1"
            >
              Test
            </CBadge>
          </td>
        </template>

        <template #representative="{item}">
          <td>
            {{
              `${item.representative.firstName} ${item.representative.lastName}`
            }}
          </td>
        </template>

        <template #ringbaAccountId="{item}">
          <td>
            {{ item.ringbaAccountId }}
          </td>
        </template>

        <template #accountManager="{item}">
          <td>
            {{ item.accountManager? item.accountManager.displayName : 'N/A' }}
          </td>
        </template>

        <template #country="{item}">
          <td>
            {{ item.companyAddress.countryCode }}
          </td>
        </template>

        <template #registrationDate="{item}">
          <td>
            {{ item.formattedRegistrationDate }}
          </td>
        </template>

        <template #adminApprovalDate="{item}">
          <td>
            {{ item.formattedAdminApprovalDate }}
          </td>
        </template>

        <template #onboardingStatus="{item}">
          <td>
            <div v-if="!item.editMode">
              <CBadge :color="getOnboardingStatusBadge(item.onboardingStatus)">
                {{ item.onboardingStatus }}
              </CBadge>
              <span @click="showOnboardingStatusSelect(item)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2"
                  name="cil-pencil"
                />
              </span>
            </div>
            <div v-if="item.editMode">
              <CSelect
                v-if="type === 'buyer'"
                :value.sync="item.onboardingStatus"
                :options="buyerOnboardingStatusCodes"
                placeholder="Please select"
              />

              <CSelect
                v-if="type === 'seller'"
                :value.sync="item.onboardingStatus"
                :options="sellerOnboardingStatusCodes"
                placeholder="Please select"
              />

              <span @click="updateOnboardingStatus(item)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2 text-success"
                  name="cil-check-circle"
                />
              </span>
              <span @click="cancelUpdate(item)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2 text-danger"
                  name="cil-x-circle"
                />
              </span>
            </div>
          </td>
        </template>

        <template #Actions="{item}">
          <td>
            <div class="d-flex align-items-center">
              <CButton
                color="info"
                size="sm"
                :to="`/dashboard/accounts/${type}/${item.id}`"
              >
                View
              </CButton>
              <CDropdown
                v-if="item.enabled === true"
                class="ml-2"
                color="warning"
                size="sm"
              >
                <template #toggler-content>
                  <span>Disable</span>
                </template>
                <CDropdownItem @click="showDisableAccountModal(item, 'Paused')">
                  Pause
                </CDropdownItem>
                <CDropdownItem @click="showDisableAccountModal(item, 'Deactivated')">
                  Deactivate
                </CDropdownItem>
                <CDropdownItem @click="showDisableAccountModal(item, 'Banned')">
                  Ban
                </CDropdownItem>
              </CDropdown>
              <CButton
                v-if="item.enabled === false"
                class="ml-2"
                color="success"
                size="sm"
                :disabled="item.disableReason === 'Deactivated' || item.disableReason === 'Banned'"
                @click="showEnableAccountModal(item)"
              >
                Enable
              </CButton>
            </div>
          </td>
        </template>
      </CDataTable>
    </div>

    <CModal
      title="Confirm disable"
      color="warning"
      data-cy="disable-account-modal"
      :show.sync="disableAccountModal"
    >
      Are you sure you want to {{ selectedDisableReason === 'Paused' ? 'pause' : selectedDisableReason === 'Deactivated' ? 'deactivate' : 'ban' }} this account?

      <CTextarea
        v-model="remarks"
        class="mt-2"
        name="Remarks"
        label="Remarks"
        :is-valid="areRemarksValid"
        invalid-feedback="Remarks are mandatory for disabling"
        small
        required
        rows="3"
        :debounce="500"
        description="Remarks will be sent in the email notification to the client"
      />

      <CTextarea
        v-if="selectedAccount !== null"
        v-model="accountNameNote"
        class="mt-2"
        name="AccountNameNote"
        :label="'Enter account name for confirmation - ' + selectedAccount.name"
        :is-valid="isAccountNameInputAsExpected(selectedAccount)"
        invalid-feedback="Enter account name (company name) to confirm account disabling"
        small
        required
        rows="1"
        :debounce="500"
      />

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isLoading"
          :disabled="!isAccountNameInputAsExpected(selectedAccount) || !areRemarksValid"
          @click="confirmDisableAccount(selectedAccount);"
        >
          Disable
        </ButtonLoading>

        <CButton
          color="danger"
          @click="disableAccountModal = false; accountNameNote = ''; remarks = '';"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Confirm enable"
      color="success"
      data-cy="enable-account-modal"
      :show.sync="enableAccountModal"
    >
      Are you sure you want to enable this account?      

      <CTextarea
        v-model="remarks"
        class="mt-2"
        name="Remarks"
        label="Remarks"
        :is-valid="areRemarksValid"
        invalid-feedback="Remarks are mandatory for enabling"
        small
        required
        rows="3"
        :debounce="500"
        description="Remarks will be sent in the email notification to the client"
      />

      <CTextarea
        v-if="selectedAccount !== null"
        v-model="accountNameNote"
        class="mt-2"
        name="AccountNameNote"
        :label="'Enter account name for confirmation - ' + selectedAccount.name"
        :is-valid="isAccountNameInputAsExpected(selectedAccount)"
        invalid-feedback="Enter account name (company name) to confirm account enabling"
        small
        required
        rows="1"
        :debounce="500"
      />

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isLoading"
          :disabled="!isAccountNameInputAsExpected(selectedAccount) || !areRemarksValid"
          @click="confirmEnableAccount(selectedAccount);"
        >
          Enable
        </ButtonLoading>

        <CButton
          color="danger"
          @click="enableAccountModal = false; accountNameNote = ''; remarks = '';"
        >
          Cancel
        </CButton>
      </template>
    </CModal>    
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { dateTimeFormat } from '@/utils'
import { cloneDeep } from 'lodash'
import { showSuccessMessage } from '@/notification-utils'
import booleanBadgeMixin from '@/mixins/boolean-badge-mixin'

export default {
  name: 'AccountsList',

  mixins: [booleanBadgeMixin],

  props: {
    accounts: {
      type: Array,
      default() {
        return []
      }
    },

    isLoadingList: {
      type: Boolean,
      default: false
    },

    type: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      enableAccountModal: false,
      disableAccountModal: false,
      isLoading: false,
      accountsCopy: [],
      enableAutoreload: true,
      searchAccountName: '',
      selectedAccount: null,
      accountNameNote: '',
      accountAction: '',
      remarks: '',
      filterInput: '',
      accountManagersList: [],
      filterAccountManager: '',
      filterDisableReason: 'All',
      selectedDisableReason: '',
      disableReasonFilterOptions: [
        { value: 'All', label: 'All' },
        { value: 'Active', label: 'Active' },
        { value: 'Disabled', label: 'Disabled' },
        { value: 'Paused', label: 'Paused' },
        { value: 'Deactivated', label: 'Deactivated' },
        { value: 'Banned', label: 'Banned' }
      ],
      fields: [
        { key: 'onboardingStatus', label: 'Status' },
        { key: 'name', label: 'Company', searchable: true },
        { key: 'ringbaAccountId', label: 'Ringba Account Id', sorter: false },
        { key: 'accountManager', label: 'Account Manager', searchable: true },
        { key: 'representative', label: 'Representative', searchable: true },
        { key: 'email', label: 'Email', sorter: true, searchable: true },
        { key: 'country', label: 'Country' },
        { key: 'registrationDate', label: 'Registration Date' },
        { key: 'adminApprovalDate', label: 'Final Approval Date' },
        { key: 'Actions', filter: false },
        { key: 'id', label: 'ID', sortable: true, searchable: true }
      ]
    }
  },

  computed: {
    ...mapState('accounts', [
      'buyerOnboardingStatusCodes',
      'sellerOnboardingStatusCodes'      
    ]),

    ...mapState('accountManagers', [ 'accountManagers' ]),

    filteredAccounts() {
      let filtered = this.accountsCopy.filter(item => {
        return this.fields.some(field => {
          if (!field.searchable) {
            return false
          }

          let keyValue = ''

          if (field.key === 'accountManager') {
            keyValue = item.accountManager?.displayName
          } else if (field.key === 'representative') {
            keyValue = item.representative?.firstName + item.representative?.lastName
          } else {
            keyValue = field.key.split('.').reduce((obj, prop) => obj?.[prop], item)
          }

          if (keyValue) {
            return keyValue.toLowerCase().includes(this.filterInput.toLowerCase())
          } else {
            return false
          }
        })
      })

      // Apply disable reason filter
      if (this.filterDisableReason && this.filterDisableReason !== 'All') {
        const disableReasonFilters = {
          'Active': acc => acc.enabled === true,
          'Disabled': acc => acc.enabled === false,
          'Paused': acc => acc.enabled === false && acc.disableReason === 'Paused',
          'Deactivated': acc => acc.enabled === false && acc.disableReason === 'Deactivated',
          'Banned': acc => acc.enabled === false && acc.disableReason === 'Banned'
        }
        const filterFn = disableReasonFilters[this.filterDisableReason]
        if (filterFn) {
          filtered = filtered.filter(filterFn)
        }
      }

      // Apply account manager filter
      if (!this.filterAccountManager || this.filterAccountManager === 'All') {
        return filtered
      } else if (this.filterAccountManager === 'NotAssigned') {
        return filtered.filter(acc => !acc.accountManager)
      } else {
        return filtered.filter(acc => acc.accountManager && acc.accountManager.id === this.filterAccountManager)
      }
    },
  },    

  watch: {
    accounts: {
      immediate: true,

      handler(newValue) {
        this.accountsCopy = cloneDeep(newValue).map(a => {
          a.editMode = false
          a.email = a.representative.email
          a.formattedRegistrationDate = dateTimeFormat.format(new Date(a.registrationDate))
          a.formattedAdminApprovalDate = !a.adminApprovalDate || new Date(a.adminApprovalDate).getFullYear() === 1 ? '' : dateTimeFormat.format(new Date(a.adminApprovalDate))
          return a
        })
      }
    }
  },

  async created() {
    document.title = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)} Accounts`

    await this.fetchAccountManagers()
    this.setAccountManagersList()

    this.filterAccountManager = this.$route.query?.accountManager
  },

  methods: {
    ...mapActions('accounts', [
      'updateBuyerAccountDetails',
      'updateSellerAccountDetails',
      'disableAccount',
      'enableAccount'
    ]),

    ...mapActions('accountManagers', [
      'fetchAccountManagers'
    ]),

    getOnboardingStatusBadge(status) {
      switch (status) {
        case 'Completed':
          return 'success'      

        case 'Pending':
        case 'PendingDocumentSigning':
        default:
          return 'warning'
      }
    },

    showOnboardingStatusSelect(item) {
      item.editMode = true
      item.originalStatus = item.onboardingStatus
    },

    async updateOnboardingStatus(item) {
      const typeToFunctionMap = {
        buyer: this.updateBuyerAccountDetails,
        seller: this.updateSellerAccountDetails
      }
      const updateFunction = typeToFunctionMap[this.type]
      if (updateFunction) {
        const accountId = item.id
        const onboardingStatus = item.onboardingStatus
        try {
          await updateFunction({
            accountId,
            updateObject: {
              onboardingStatus
            }
          })
          item.editMode = false
        } catch (error) {
          // Revert to original status on failure
          item.onboardingStatus = item.originalStatus
          item.editMode = false
          throw error
        }
      }
    },

    cancelUpdate(item) {
      item.onboardingStatus = item.originalStatus
      item.editMode = false
    },

    async confirmDisableAccount(account) {
      try {
        this.isLoading = true
        await this.disableAccount({
          accountId: account.id,
          type: this.type,
          remarks: this.remarks,
          disableReason: this.selectedDisableReason
        })

        showSuccessMessage('Successfully disabled account ' + account.id)
        this.disableAccountModal = false
      } catch (error) {
        //
      } finally {
        this.isLoading = false
        this.accountNameNote = ''
        this.remarks = ''
        this.selectedDisableReason = ''
      }
    },

    async confirmEnableAccount(account) {
      try {
        this.isLoading = true
        await this.enableAccount({accountId: account.id, type: this.type, remarks: this.remarks})

        showSuccessMessage('Successfully enabled account ' + account.id)
        this.enableAccountModal = false
      } catch (error) {
        //
      } finally {
        this.isLoading = false
        this.accountNameNote = ''
        this.remarks = ''
      }
    },

    showDisableAccountModal(account, disableReason) {
      this.accountAction = 'disable'
      this.selectedAccount = account
      this.selectedDisableReason = disableReason
      this.disableAccountModal = true
    },

    showEnableAccountModal(account) {
      this.accountAction = 'enable'
      this.selectedAccount = account
      this.enableAccountModal = true
    },

    isAccountNameInputAsExpected(account) {
      if (this.accountAction === 'enable' && this.accountNameNote === account.name) {
        return true
      }

      if (this.accountAction === 'disable' && this.accountNameNote === account.name) {
        return true
      }

      return false
    },

    areRemarksValid() {
      if (this.remarks.length) {
        return true
      } else {
        return false
      }
    },

    getDisableReasonBadgeColor(disableReason) {
      switch (disableReason) {
        case 'Paused':
          return 'warning'
        case 'Deactivated':
          return 'dark'
        case 'Banned':
          return 'danger'
        default:
          return 'secondary'
      }
    },

    setAccountManagersList() {

      const accountManagersResponse = this.accountManagers.map(am => {
        return {
          value: am.id,
          label: am.displayName
        }
      })

      accountManagersResponse.unshift({
        value: 'NotAssigned',
        label: 'Not assigned'
      })

      accountManagersResponse.unshift({
        value: 'All',
        label: 'All'
      })

      this.accountManagersList = accountManagersResponse
    }
  }
}
</script>

<style></style>
